/**
 * ClientCard — карточка клиента (drawer справа, design Handoff 2). Шапка
 * (аватар, статус, телефон+копия, ID, WhatsApp, ⋯-меню) + табы Обзор /
 * Сотрудники / Платежи / Заметки / Ассистент / Расход. Клиника — все табы;
 * соло — без «Сотрудников».
 *
 * Держит модалки (данные, врачи, оплата и блокировка подписки, блокировка
 * аккаунта) и тост. Детали грузятся по id. Клик по строке списка открывает карточку.
 */

import {useEffect, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Archive, Copy, MessageCircle, MoreVertical, Phone, Tags, X} from 'lucide-react';

import {unblockAccount} from '@services/accounts';
import {
  clientDetailQueryOptions,
  type ClientListItem,
  removeDoctor,
  type StaffMember,
} from '@services/clients';
import {keys} from '@queries/keys';
import {useToast} from '@hooks/useToast';
import {StatusPill} from '@components/StatusPill';
import {Toast} from '@components/Toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@components/ui/DropdownMenu';
import {Loader} from '@components/ui/Loader';
import {Sheet, SheetContent} from '@components/ui/Sheet';
import {cn} from '@lib/utils';
import {BlockAccountModal} from '@pages/Accounts/BlockAccountModal';
import {ClientTypeIcon} from '@pages/Clients/ClientTypeIcon';

import {ActivateSubscriptionModal} from './ActivateSubscriptionModal';
import {AddDoctorModal} from './AddDoctorModal';
import {ArchiveClientModal} from './ArchiveClientModal';
import {BlockSubscriptionModal} from './BlockSubscriptionModal';
import {BotTab} from './BotTab';
import {ChangePhoneModal} from './ChangePhoneModal';
import {EditDoctorModal} from './EditDoctorModal';
import {EditMainModal} from './EditMainModal';
import {LedgerTab} from './LedgerTab';
import {NotesTab} from './NotesTab';
import {OverviewTab} from './OverviewTab';
import {PaymentsTab} from './PaymentsTab';
import {type StaffAccountRef, StaffTab} from './StaffTab';
import {UsageTab} from './UsageTab';

type Tab = 'overview' | 'staff' | 'payments' | 'ledger' | 'notes' | 'bot' | 'usage';
type Modal =
  | null
  | 'otp'
  | 'editMain'
  | 'addDoctor'
  | 'editDoctor'
  | 'archive'
  | 'activate'
  | 'blockSub'
  | 'blockAccount';

type ClientCardProps = {
  client: ClientListItem | null;
  onClose: () => void;
};

export const ClientCard = ({client, onClose}: ClientCardProps) => {
  const [snap, setSnap] = useState<ClientListItem | null>(client);
  const [tab, setTab] = useState<Tab>('overview');
  const [modal, setModal] = useState<Modal>(null);
  /** Radix: клик, закрывающий модалку, долетает до шторки как «клик снаружи»
      и закрывал весь дравер. Метка времени глушит это ложное закрытие. */
  const modalCloseAtRef = useRef(0);
  const closeModal = () => {
    modalCloseAtRef.current = Date.now();
    setModal(null);
  };
  const [editingDoctor, setEditingDoctor] = useState<StaffMember | null>(null);
  /* Для блокировки хватает id аккаунта и имени — остальное из `Account`
     здесь не нужно, а требовать его означало бы тащить лишний запрос
     ради полей, которые никто не покажет. */
  const [blockingAccount, setBlockingAccount] = useState<StaffAccountRef | null>(null);
  const {toast, notify} = useToast();

  /** snap живёт и во время анимации закрытия; свежий client (после рефетча
      списка) протекает сюда сразу. Таб сбрасываем только при смене клиента. */
  const prevClientId = useRef<string | null>(null);
  useEffect(() => {
    if (client) {
      setSnap(client);
      if (prevClientId.current !== client.id) {
        setTab('overview');
        prevClientId.current = client.id;
      }
    }
  }, [client]);

  const queryClient = useQueryClient();
  const unblock = useMutation({
    mutationFn: unblockAccount,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.accounts.all});
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      notify('Доступ возвращён — войти нужно заново');
    },
  });
  const deleteDoctor = useMutation({
    mutationFn: removeDoctor,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      notify('Врач удалён');
    },
  });

  const detailQ = useQuery({
    ...clientDetailQueryOptions(snap?.id ?? ''),
    enabled: !!snap,
  });
  const detail = detailQ.data;

  if (!snap) return <Sheet open={false} onOpenChange={() => onClose()} />;

  const isClinic = snap.type === 'clinic';
  const status = snap.subscriptionStatus;

  const tabs: {key: Tab; label: string}[] = [
    {key: 'overview', label: 'Обзор'},
    ...(isClinic ? [{key: 'staff' as const, label: 'Сотрудники'}] : []),
    {key: 'payments', label: 'Платежи'},
    {key: 'ledger', label: 'Счёт'},
    {key: 'notes', label: 'Заметки'},
    {key: 'bot', label: 'Ассистент'},
    {key: 'usage', label: 'Расход'},
  ];

  const handleBlockAccount = (a: StaffAccountRef) => {
    setBlockingAccount(a);
    setModal('blockAccount');
  };

  const copyPhone = () => {
    navigator.clipboard?.writeText(snap.adminPhone).then(() => notify('Телефон скопирован'));
  };

  return (
    <>
      <Sheet
        open={!!client}
        onOpenChange={(o) => {
          if (o) return;
          if (modal !== null || Date.now() - modalCloseAtRef.current < 400) return;
          onClose();
        }}
      >
        <SheetContent
          side="right"
          showClose={false}
          // Пока открыта модалка правки — клики по ней не закрывают дравер
          onInteractOutside={(e) => {
            if (modal !== null) e.preventDefault();
          }}
          className="flex w-[524px] max-w-[96vw] flex-col gap-0 bg-[#F7F8FA] p-0"
        >
          {/* header */}
          <div className="border-b border-skr-line bg-card px-[22px] pt-4">
            <div className="flex items-center justify-between">
              <button
                onClick={onClose}
                aria-label="Закрыть"
                className="grid size-[34px] place-items-center rounded-[9px] bg-secondary text-skr-ink"
              >
                <X className="size-[18px]" strokeWidth={2} />
              </button>
              <div className="flex gap-2">
                <a
                  href={`https://wa.me/${snap.adminPhone.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-9 items-center gap-[7px] rounded-[9px] border border-[#DDE7DC] bg-card px-[13px] text-[13px] font-semibold text-skr-paid hover:bg-[#F1FAF3]"
                >
                  <MessageCircle className="size-4" strokeWidth={2} />
                  WhatsApp
                </a>
                <DropdownMenu>
                  <DropdownMenuTrigger className="grid size-9 place-items-center rounded-[9px] border border-input bg-card text-muted-foreground outline-none">
                    <MoreVertical className="size-[18px]" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => setModal('otp')}>
                      <Phone />
                      Сменить номер админа
                    </DropdownMenuItem>
                    {isClinic && (
                      <DropdownMenuItem onClick={() => notify('Изменение тарифа — скоро')}>
                        <Tags />
                        Изменить тариф
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setModal('archive')}
                    >
                      <Archive />
                      Архивировать клиента
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3.5">
              <ClientTypeIcon type={snap.type} size={52} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-[9px]">
                  <h2 className="truncate text-[19px] font-bold -tracking-[.01em]">{snap.name}</h2>
                  <StatusPill status={status} trialDay={snap.trialDay} />
                </div>
                <div className="mt-1 flex items-center gap-2 text-[13px] text-muted-foreground">
                  <span>
                    {isClinic ? 'Клиника' : 'Соло'} · {snap.cityName}
                  </span>
                  <span className="text-skr-faint">·</span>
                  <span className="font-mono">{snap.adminPhone}</span>
                  <button
                    onClick={copyPhone}
                    title="Копировать телефон"
                    className="grid size-[22px] place-items-center rounded-[5px] text-skr-faint hover:bg-muted hover:text-muted-foreground"
                  >
                    <Copy className="size-[13px]" strokeWidth={1.9} />
                  </button>
                </div>
              </div>
            </div>

            {/* tabs */}
            <div className="mt-4 flex gap-[14px] overflow-x-auto [scrollbar-width:none]">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={cn(
                    'shrink-0 border-b-2 py-2.5 text-[13.5px] font-semibold',
                    tab === t.key
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* body */}
          <div className="flex-1 overflow-y-auto px-[22px] pb-[30px] pt-[18px]">
            {!detail ? (
              <div className="flex min-h-[280px] items-center justify-center">
                <Loader label="Загружаем карточку…" />
              </div>
            ) : (
              <>
                {tab === 'overview' && (
                  <OverviewTab
                    client={snap}
                    detail={detail}
                    onActivate={() => setModal('activate')}
                    onBlock={() => setModal('blockSub')}
                    onEditMain={() => setModal('editMain')}
                    onChangeTariff={() => notify('Изменение тарифа — скоро')}
                  />
                )}
                {tab === 'staff' && (
                  <StaffTab
                    client={snap}
                    detail={detail}
                    onAddDoctor={() => setModal('addDoctor')}
                    onEditDoctor={(d) => {
                      setEditingDoctor(d);
                      setModal('editDoctor');
                    }}
                    onDeleteDoctor={(d) => deleteDoctor.mutate({clientId: snap.id, doctorId: d.id})}
                    onBlockAccount={handleBlockAccount}
                    onUnblockAccount={(a) => unblock.mutate(a.id)}
                  />
                )}
                {tab === 'payments' && <PaymentsTab detail={detail} client={snap} />}
                {tab === 'ledger' && (
                  <LedgerTab clientId={snap.id} clientName={snap.name} onSaved={notify} />
                )}
                {tab === 'notes' && <NotesTab clientId={snap.id} detail={detail} />}
                {tab === 'bot' && <BotTab clientId={snap.id} detail={detail} onSaved={notify} />}
                {tab === 'usage' && <UsageTab clientId={snap.id} />}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <ChangePhoneModal
        open={modal === 'otp'}
        clientId={snap.id}
        onClose={closeModal}
        onSaved={() => {
          closeModal();
          notify('Номер обновлён');
        }}
      />
      <EditMainModal
        open={modal === 'editMain'}
        client={snap}
        detail={detail ?? null}
        onClose={closeModal}
        onSaved={() => {
          closeModal();
          notify('Данные сохранены');
        }}
      />
      <AddDoctorModal
        open={modal === 'addDoctor'}
        clientId={snap.id}
        seatsLeft={Math.max(0, (snap.seatsLimit ?? 0) - snap.seatsUsed)}
        seatsLimit={snap.seatsLimit ?? 0}
        onClose={closeModal}
        onSaved={() => {
          closeModal();
          notify('Врач добавлен');
        }}
      />
      <ArchiveClientModal
        open={modal === 'archive'}
        clientId={snap.id}
        clientName={snap.name}
        onClose={closeModal}
        onArchived={() => {
          closeModal();
          onClose();
          notify('Клиент архивирован');
        }}
      />
      <EditDoctorModal
        open={modal === 'editDoctor'}
        clientId={snap.id}
        doctor={editingDoctor}
        onClose={closeModal}
        onSaved={() => {
          closeModal();
          notify('Изменения сохранены');
        }}
      />

      <ActivateSubscriptionModal
        open={modal === 'activate'}
        clientId={snap.id}
        clientName={snap.name}
        currentPeriodEnd={snap.currentPeriodEnd}
        onClose={closeModal}
        onSaved={(until) => {
          closeModal();
          notify(`Доступ открыт до ${until.split('-').reverse().join('.')}`, 2200);
        }}
      />
      <BlockSubscriptionModal
        open={modal === 'blockSub'}
        clientId={snap.id}
        clientName={snap.name}
        onClose={closeModal}
        onSaved={() => {
          closeModal();
          notify('Доступ закрыт — клиника в режиме чтения', 2200);
        }}
      />
      <BlockAccountModal
        account={modal === 'blockAccount' ? blockingAccount : null}
        onClose={closeModal}
        onSaved={() => {
          closeModal();
          notify('Аккаунт заблокирован, сессии погашены', 2200);
        }}
      />

      <Toast message={toast} />
    </>
  );
};
